/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { PRODUCT_LIST_DEFAULT_PAGE_SIZE } from '@/data/constants/catalog';
import { HDM_SEARCH_SEO_PATHS } from '@/data/constants/urlPathConstants';
import { ProductFacet } from '@/data/types/Product';
import { getStoreDataByPhysicalStoreId } from '@/data/utils/commonIntegrationUtils';
import {
	friendlyQueryToElasticFacets,
	SORT_URL_KEY,
	sortSlugToOrderBy,
} from '@/data/utils/facetUrlMapper';
import { ParsedUrlQuery } from 'querystring';
import { DEFAULT_STORE_ID } from 'shared/custom/constants/commonConstants';

// decodeURIComponent throws "URIError: URI malformed" on a bare "%" or an incomplete
// escape sequence (e.g. searching "50%"). Fall back to the raw value so the search still runs.
const safeDecodeURIComponent = (value: string): string => {
	try {
		return decodeURIComponent(value);
	} catch {
		return value;
	}
};

export const resolveSearchTerm = ({ path }: ParsedUrlQuery): string | undefined => {
	const rawPath = Array.isArray(path) ? path : path ? [path] : [];
	const termSegments =
		rawPath[0] === HDM_SEARCH_SEO_PATHS.Search ? rawPath.slice(1) : rawPath.slice(-1);
	const resolvedPath = termSegments.join('/').replaceAll('+', ' ') || undefined;

	return resolvedPath;
};

const resolveOrderBy = (query: ParsedUrlQuery): string => {
	const raw = query[SORT_URL_KEY];
	const slug = Array.isArray(raw) ? raw[0] : raw;
	if (slug) {
		const mapped = sortSlugToOrderBy(slug);
		if (mapped) return mapped;
	}
	const legacy = query.orderBy;
	return (Array.isArray(legacy) ? legacy[0] : legacy) ?? '0';
};

const resolveOffset = (raw: string | string[] | undefined): number => {
	const n = Number(raw ?? 0);
	if (Number.isNaN(n) || n <= 1) return 0;
	return (n - 1) * PRODUCT_LIST_DEFAULT_PAGE_SIZE;
};

const resolveFacets = (query: ParsedUrlQuery, facets?: ProductFacet[]): string[] => {
	const friendly = friendlyQueryToElasticFacets(query, facets);
	const { facet } = query;
	const legacy = facet === undefined ? [] : Array.isArray(facet) ? facet : [facet];
	return [...legacy, ...friendly];
};

// Same-day delivery filter: only forwarded to the search API when explicitly "true".
const resolveSameDay = ({ sameDay }: ParsedUrlQuery) =>
	(Array.isArray(sameDay) ? sameDay[0] : sameDay) === 'true' ? { sameDay: 'true' } : {};

export const getSearchTerm = (query: ParsedUrlQuery) => {
	const isSearchPage = query.path?.[0] === HDM_SEARCH_SEO_PATHS.Search;
	const searchTerm = isSearchPage ? resolveSearchTerm(query) : undefined;
	return searchTerm;
};

export const getProductListQueryParameters = (query: ParsedUrlQuery, facets?: ProductFacet[]) => {
	const { physicalStoreId = DEFAULT_STORE_ID, stLocId, facetLimit, minPrice, maxPrice } = query;
	const { marketId } = getStoreDataByPhysicalStoreId(physicalStoreId);
	const mergedFacets = resolveFacets(query, facets);
	const searchTerm = getSearchTerm(query);

	return {
		offset: resolveOffset(query.pag),
		limit: PRODUCT_LIST_DEFAULT_PAGE_SIZE,
		marketId,
		stLocId,
		physicalStoreId: +physicalStoreId,
		orderBy: resolveOrderBy(query),
		...(facetLimit !== undefined && { facetLimit }),
		...(minPrice !== undefined && { minPrice: Number(minPrice) }),
		...(maxPrice !== undefined && { maxPrice: Number(maxPrice) }),
		...(mergedFacets.length > 0 && { facet: mergedFacets }),
		...resolveSameDay(query),
		...(searchTerm !== undefined && {
			searchTerm: safeDecodeURIComponent(searchTerm.toString()),
		}),
	};
};

export const getFacetsQueryParameters = (query: ParsedUrlQuery) => {
	const { physicalStoreId = DEFAULT_STORE_ID, stLocId, facetLimit } = query;
	const { marketId } = getStoreDataByPhysicalStoreId(physicalStoreId);
	const searchTerm = getSearchTerm(query);

	return {
		offset: 0,
		limit: 1,
		marketId,
		stLocId,
		physicalStoreId: +physicalStoreId,
		// Keep facetLimit in sync with the products query so allFacets contains the expanded
		// values — friendlyQueryToElasticFacets needs them to resolve a selected expanded facet.
		...(facetLimit !== undefined && { facetLimit }),
		...resolveSameDay(query),
		...(searchTerm !== undefined && {
			searchTerm: safeDecodeURIComponent(searchTerm.toString()),
		}),
	};
};
